import { ReactElement, memo, useState, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"

import { Colors } from "themes/palette"
import { validateEmail } from "utils/auth/authHelper"
import { loginUser } from "reducers/user/user-actions"
import { useAppDispatch, useAppSelector } from "reducers/store"
import ShelfLogo from "./ShelfLogoSvg"
import Input from "components/EvtKit/input/Input"
import Button from "components/EvtKit/button/Button"
import clsx from "clsx"
import Heading from "components/Typography/Heading"
import Toast from "components/EvtKit/Toast/ToastComponent"
import ConfirmLogin from "./ConfirmLogin"

interface ContainerProps {
  w?: string
  h?: string
  direction?: string
  justify?: string
  align?: string
  mb?: string
  bgColor?: string
  borderRadius?: string
}

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.palette.white};
`

const Container = styled.div<ContainerProps>`
  margin-top: 48px;
  display: flex;
  width: ${(props) => props.w || "auto"};
  height: ${(props) => props.h || "auto"};
  flex-direction: ${(props) => props.direction || "row"};
  justify-content: ${(props) => props.justify || ""};
  align-items: ${(props) => props.align || ""};
  margin-bottom: ${(props) => props.mb || "0"};
  background-color: ${(props) => props.bgColor || "transparent"};
  border-radius: ${(props) => props.borderRadius || "8px"};
`

const FormLogin = styled.form`
  width: 420px;
  background: ${(props) => props.theme.palette.white};
  border-radius: 20px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.3);

  .divider {
    width: 100%;
  }
`

const ContainerFields = styled.div`
  margin-top: 32px;
`

export interface EventProps {
  preventDefault: () => void
}

export type StepTypes = "login" | "confirmLogin"

export const steps: Record<string, StepTypes> = {
  login: "login",
  confirmLogin: "confirmLogin"
}

const Login = (): ReactElement => {
  const [token, setToken] = useState<string | undefined>()
  const [step, setStep] = useState<StepTypes>(steps.login)

  const appDispatch = useAppDispatch()
  const navigate = useNavigate()

  const { loading, error } = useAppSelector(({ user: { userLogin } }) => userLogin)

  const [formState, setFormState] = useState({
    email: "",
    password: ""
  })
  const [errors, setErrors] = useState({
    email: false,
    password: false
  })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target

    if (name === "email") {
      setErrors((prevState) => ({
        ...prevState,
        ["email"]: value.length > 0 && !validateEmail(value)
      }))
    }

    setFormState((prevState) => ({
      ...prevState,
      [name]: value
    }))
  }

  const isDisabled = useMemo(() => {
    const isValid = validateEmail(formState.email) && formState.password.length > 0

    return !isValid
  }, [formState])

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const { email, password } = formState

      const token = await appDispatch(loginUser(email, password))

      if (token) {
        setToken(token)
        setStep(steps.confirmLogin)
      }
    },
    [formState, appDispatch, navigate]
  )

  const isLoginStep = step === steps.login
  const isConfirmStep = step === steps.confirmLogin

  return (
    <PageContainer>
      <Container mb="30px" bgColor={Colors.primary_one}>
        <ShelfLogo />
      </Container>
      {isLoginStep && (
        <>
          {!!error && <Toast variant="error">Usuário ou senha inválidos</Toast>}
          <FormLogin onSubmit={handleSubmit}>
            <Heading variant="h5" color="primary-dark">
              Admin
            </Heading>
            <ContainerFields>
              <Input
                id="email"
                type="email"
                name="email"
                onChange={handleChange}
                error={errors.email ? "Email inválido" : ""}
                label="Digite seu e-mail"
                safeBottom
                value={formState.email}
              />
              <Input
                id="password"
                type="password"
                name="password"
                onChange={handleChange}
                label="Digite sua senha"
                value={formState.password}
              />
              <Container justify="center" mb="20px">
                <Button
                  disabled={isDisabled}
                  className={clsx("fill-width", { disabled: isDisabled })}
                  type="submit"
                  label="Entrar"
                  loading={loading}
                />
              </Container>
            </ContainerFields>
          </FormLogin>
        </>
      )}

      {isConfirmStep && <ConfirmLogin token={token} email={formState.email} setToken={setToken} setStep={setStep} />}
    </PageContainer>
  )
}

export default memo(Login)
